"use client"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AlertTriangle } from "lucide-react"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"

import type { AppointmentRecord } from "@/lib/types/appointment"

interface NotificationsSectionProps {
  appointment?: AppointmentRecord | null
}

export function NotificationsSection({ appointment }: NotificationsSectionProps) {
  const { t } = useTranslation()
  const [confirmationChannels, setConfirmationChannels] = useState({
    sms: false,
    email: false,
    vkPush: false,
    yplaces: false,
  })

  const [reminderChannels, setReminderChannels] = useState({
    sms: false,
    email: false,
  })

  const [messageChannels, setMessageChannels] = useState({
    sms: false,
    push: false,
    email: false,
  })

  const [messageText, setMessageText] = useState("")

  const toggleConfirmationChannel = (channel: keyof typeof confirmationChannels) => {
    setConfirmationChannels((prev) => ({
      ...prev,
      [channel]: !prev[channel],
    }))
  }

  const toggleReminderChannel = (channel: keyof typeof reminderChannels) => {
    setReminderChannels((prev) => ({
      ...prev,
      [channel]: !prev[channel],
    }))
  }

  const toggleMessageChannel = (channel: keyof typeof messageChannels) => {
    setMessageChannels((prev) => ({
      ...prev,
      [channel]: !prev[channel],
    }))
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      {/* Warning banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
        <div className="flex">
          <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">{t("appointment.notifications.no_consent", "Client has no consent for notifications")}</p>
            <p className="text-xs text-gray-600 mt-1">
              {t("appointment.notifications.learn_how", "Learn how to collect and enter them in the")}{" "}
              <a href="#" className="text-blue-600 hover:underline">
                {t("appointment.notifications.knowledge_base", "Knowledge Base")}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Confirmation section */}
      <div className="mb-8">
        <h3 className="text-base font-medium mb-2">{t("appointment.notifications.booking_confirmation", "Booking confirmation")}</h3>
        <p className="text-xs text-gray-600 mb-4">{t("appointment.notifications.confirmation_description", "Notification will be sent to clients via selected channels")}</p>

        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            variant={confirmationChannels.sms ? "default" : "outline"}
            size="sm"
            className={`text-xs h-8 ${confirmationChannels.sms ? "bg-gray-800" : ""}`}
            onClick={() => toggleConfirmationChannel("sms")}
          >
            SMS
          </Button>
          <Button
            variant={confirmationChannels.email ? "default" : "outline"}
            size="sm"
            className={`text-xs h-8 ${confirmationChannels.email ? "bg-gray-800" : ""}`}
            onClick={() => toggleConfirmationChannel("email")}
          >
            Email
          </Button>
          <Button
            variant={confirmationChannels.vkPush ? "default" : "outline"}
            size="sm"
            className={`text-xs h-8 ${confirmationChannels.vkPush ? "bg-gray-800" : ""}`}
            onClick={() => toggleConfirmationChannel("vkPush")}
          >
            VK Push
          </Button>
          <Button
            variant={confirmationChannels.yplaces ? "default" : "outline"}
            size="sm"
            className={`text-xs h-8 ${confirmationChannels.yplaces ? "bg-gray-800" : ""}`}
            onClick={() => toggleConfirmationChannel("yplaces")}
          >
            YPLACES
          </Button>
        </div>

        <Button variant="outline" size="sm" className="text-xs h-8">
          {t("common.send", "Send")}
        </Button>
      </div>

      {/* Reminder section */}
      <div className="mb-8">
        <h3 className="text-base font-medium mb-4">{t("appointment.notifications.visit_reminder", "Visit reminder")}</h3>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-xs text-gray-600 mb-2">{t("appointment.notifications.delivery_method", "Delivery method")}</p>
            <div className="space-y-3">
              <div className="flex items-center">
                <Checkbox
                  id="reminder-sms"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={reminderChannels.sms}
                  onCheckedChange={() => toggleReminderChannel("sms")}
                />
                <label htmlFor="reminder-sms" className="ml-2 text-sm">
                  SMS
                </label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  id="reminder-email"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={reminderChannels.email}
                  onCheckedChange={() => toggleReminderChannel("email")}
                />
                <label htmlFor="reminder-email" className="ml-2 text-sm">
                  Email
                </label>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-600 mb-2">{t("appointment.notifications.time_before_visit", "Time before visit")}</p>
            <div className="space-y-3">
              <div className="relative">
                <select className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm">
                  <option>{t("appointment.notifications.do_not_send", "Do not send")}</option>
                  <option>{t("appointment.notifications.1_hour_before", "1 hour before")}</option>
                  <option>{t("appointment.notifications.2_hours_before", "2 hours before")}</option>
                  <option>{t("appointment.notifications.3_hours_before", "3 hours before")}</option>
                  <option>{t("appointment.notifications.1_day_before", "1 day before")}</option>
                  <option>{t("appointment.notifications.2_days_before", "2 days before")}</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <div className="relative">
                <select className="w-full h-9 px-3 border border-gray-300 rounded-md appearance-none text-sm">
                  <option>{t("appointment.notifications.do_not_send", "Do not send")}</option>
                  <option>{t("appointment.notifications.1_hour_before", "1 hour before")}</option>
                  <option>{t("appointment.notifications.2_hours_before", "2 hours before")}</option>
                  <option>{t("appointment.notifications.3_hours_before", "3 hours before")}</option>
                  <option>{t("appointment.notifications.1_day_before", "1 day before")}</option>
                  <option>{t("appointment.notifications.2_days_before", "2 days before")}</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send message section */}
      <div>
        <h3 className="text-base font-medium mb-2">{t("appointment.notifications.send_message", "Send message")}</h3>
        <p className="text-xs text-gray-600 mb-2">{t("appointment.notifications.message_text", "Message text")}</p>

        <textarea
          className="w-full border border-gray-300 rounded-md p-3 min-h-[120px] text-sm mb-4"
          placeholder={t("appointment.notifications.enter_message", "Enter message text")}
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
        ></textarea>

        <p className="text-xs text-gray-600 mb-2">{t("appointment.notifications.delivery_channels", "Delivery channels")}</p>
        <div className="space-y-3 mb-4">
          <div className="flex items-center">
            <Checkbox
              id="message-sms"
              className="h-4 w-4 rounded border-gray-300"
              checked={messageChannels.sms}
              onCheckedChange={() => toggleMessageChannel("sms")}
            />
            <label htmlFor="message-sms" className="ml-2 text-sm">
              SMS
            </label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="message-push"
              className="h-4 w-4 rounded border-gray-300"
              checked={messageChannels.push}
              onCheckedChange={() => toggleMessageChannel("push")}
            />
            <label htmlFor="message-push" className="ml-2 text-sm">
              Push
            </label>
          </div>
          <div className="flex items-center">
            <Checkbox
              id="message-email"
              className="h-4 w-4 rounded border-gray-300"
              checked={messageChannels.email}
              onCheckedChange={() => toggleMessageChannel("email")}
            />
            <label htmlFor="message-email" className="ml-2 text-sm">
              Email
            </label>
          </div>
        </div>

        <Button variant="outline" size="sm" className="text-xs h-8">
          {t("appointment.notifications.send_message", "Send message")}
        </Button>
      </div>
    </div>
  )
}
